/**
 * <RecoverAbsenceFlow> — « Planifier un rattrapage », de bout en bout (template).
 *
 * Quelle absence ? → Quel créneau ? → c'est planifié. Une seule absence rattrapable : la
 * première étape n'a qu'une réponse, elle est sautée et le parcours tient en une étape.
 *
 * Contrôlé : l'étape, l'absence et le créneau choisis arrivent en props, les gestes repartent en
 * callbacks. La machine vit côté écran (`useRecoverAbsenceFlow`) — c'est ce qui rend chaque étape
 * regardable seule en story.
 *
 * Toute la copie du geste est ICI, dans ses deux voix (`TEXTS`) : le parent lit son enfant à la
 * troisième personne ; l'élève lit les mots de son formulaire legacy (`RecoverFormComponent`) —
 * « Planifier mes rattrapages », « Je choisis une séance de rattrapage », « Le rattrapage a bien
 * été planifié ». Les créneaux sont ceux de « Décaler une séance » (`<RecoverySlotStep>`), sans la
 * sortie « Je choisirai plus tard » : l'absence est déjà notée, remettre à plus tard, c'est fermer.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { AbsencePickerStep } from "../../organisms/AbsencePickerStep"
import { RecoverySlotStep } from "../../organisms/RecoverySlotStep"
import { type UIParentRecoveryAbsence, type UIParentRecoverySlot } from "../../../ports/primary/ui/types"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type Addressee } from "../../utils/addressee"
import { type Voice } from "../../utils/voice"
import { ParentFlowModal } from "../ParentFlowModal"

export type RecoverAbsenceStep = "absence" | "slot" | "done"

/** Ce que dit l'étape du choix quand aucune absence ne se rattrape encore (copie legacy, `RecoverFormComponent`). */
const NOTHING_RECOVERABLE_YET =
    "Aucun rattrapage disponible pour le moment. Une notification sera envoyée lorsqu'un rattrapage sera possible."

/** Les mots du geste, par voix. `child` est le prénom de l'enfant — vide en voix élève. */
const TEXTS: Record<
    Voice,
    {
        /** Le rappel d'une étape qui n'en a pas d'autre — « pour Damien ». */
        reminder: (child: string) => string | undefined
        absenceTitle: string
        slotTitle: string
        doneTitle: string
        /** `slotLabel` absent : le créneau n'est plus connu au moment de l'accusé. */
        done: (child: string, slotLabel?: string) => { message: string; detail?: string }
    }
> = {
    parent: {
        reminder: (child) => `pour ${child}`,
        absenceTitle: "Quelle absence ?",
        slotTitle: "Quel créneau ?",
        doneTitle: "Rattrapage planifié",
        done: (child, slotLabel) => ({
            message:
                slotLabel != null
                    ? `Le rattrapage ${elide(child)} est planifié le ${slotLabel}.`
                    : `Le rattrapage ${elide(child)} est planifié.`,
        }),
    },
    student: {
        reminder: () => undefined,
        absenceTitle: "Planifier mes rattrapages",
        slotTitle: "Je choisis une séance de rattrapage",
        doneTitle: "Effectué",
        done: (_child, slotLabel) => ({
            message: "Le rattrapage a bien été planifié",
            detail: slotLabel != null ? `Il aura lieu le ${slotLabel}.` : undefined,
        }),
    },
}

export type RecoverAbsenceFlowProps = Addressee & {
    visible: boolean
    onClose: () => void
    step: RecoverAbsenceStep

    absences: UIParentRecoveryAbsence[]
    selectedAbsence?: UIParentRecoveryAbsence | null
    onSelectAbsence: (absence: UIParentRecoveryAbsence) => void
    /** Une seule absence rattrapable : « Quelle absence ? » a été sautée, le parcours tient en une étape. */
    absenceStepSkipped?: boolean

    slots?: UIParentRecoverySlot[]
    slotsLoading?: boolean
    selectedSlot?: UIParentRecoverySlot | null
    onSelectSlot: (slot: UIParentRecoverySlot) => void
    /** Ce que dit l'étape quand aucun créneau n'est proposé — le motif du serveur. */
    slotsEmptyMessage?: string

    onSubmit?: () => void
    submitLoading?: boolean
    onBack?: () => void

    /** Un envoi refusé : l'étape reste à l'écran, avec ses créneaux. */
    errorMessage?: string
    testID?: string
}

export function RecoverAbsenceFlow(props: RecoverAbsenceFlowProps) {
    const {
        visible,
        onClose,
        step,
        voice = "parent",
        childFirstname,
        absences,
        selectedAbsence,
        onSelectAbsence,
        absenceStepSkipped = false,
        slots = [],
        slotsLoading = false,
        selectedSlot,
        onSelectSlot,
        slotsEmptyMessage,
        onSubmit,
        submitLoading = false,
        onBack,
        errorMessage,
        testID,
    } = props

    const texts = TEXTS[voice]
    const child = childFirstname ?? ""
    const totalSteps = absenceStepSkipped ? 1 : 2

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />
    const close = <Button label="Fermer" variant="outline" onPress={onClose} />

    const content = (() => {
        switch (step) {
            case "absence": {
                const anyRecoverable = absences.some((absence) => absence.recoverable)
                return {
                    stepTitle: texts.absenceTitle,
                    stepIndex: 1,
                    context: texts.reminder(child),
                    footer: anyRecoverable ? cancel : close,
                    body: (
                        <AbsencePickerStep
                            absences={absences}
                            selectedId={selectedAbsence?.id}
                            onSelect={onSelectAbsence}
                            notice={anyRecoverable ? undefined : NOTHING_RECOVERABLE_YET}
                        />
                    ),
                }
            }

            case "slot": {
                // Rien à planifier : la seule sortie est de fermer — un bouton de validation éteint
                // pour toujours ne serait qu'un bouton de trop.
                const nothingToPick = !slotsLoading && slots.length === 0
                return {
                    stepTitle: texts.slotTitle,
                    stepIndex: totalSteps,
                    context: selectedAbsence?.label ?? texts.reminder(child),
                    footer: nothingToPick ? (
                        close
                    ) : (
                        <Stack gap="2">
                            <Button
                                label="Planifier le rattrapage"
                                onPress={onSubmit}
                                loading={submitLoading}
                                disabled={selectedSlot == null || submitLoading}
                                fullWidth
                            />
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <RecoverySlotStep
                            slots={slots}
                            loading={slotsLoading}
                            selectedId={selectedSlot?.id}
                            onSelect={onSelectSlot}
                            emptyMessage={slotsEmptyMessage}
                            voice={voice}
                        />
                    ),
                }
            }

            case "done":
            default:
                return {
                    stepTitle: texts.doneTitle,
                    stepIndex: totalSteps,
                    context: texts.reminder(child),
                    footer: close,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            {...texts.done(
                                child,
                                selectedSlot != null ? formatDayTime(selectedSlot.date, false, "sentence") : undefined
                            )}
                        />
                    ),
                }
        }
    })()

    return (
        <ParentFlowModal
            visible={visible}
            onClose={onClose}
            title="Planifier un rattrapage"
            stepTitle={content.stepTitle}
            step={content.stepIndex}
            totalSteps={totalSteps}
            onBack={step === "slot" && !absenceStepSkipped ? onBack : undefined}
            context={content.context}
            errorMessage={errorMessage}
            footer={content.footer}
            testID={testID}
        >
            {content.body}
        </ParentFlowModal>
    )
}
